import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import ReactGA from "react-ga4";
import useExportToPDF from "component/htmlToPDF/export-html-pdf";
import { downloadElementAsImage } from "utils/chartExport";
import { LabelLangString } from "component/LabelLang/label-lang";
import { END_POINTS } from "constants/APIConstant";
import {
  buildChartData,
  buildColHeaders,
  findIndicatorPublication,
  findIndicatorPublicationId,
  findSelectedIndicator,
  flattenIndicators,
  getChartImageFilename,
  getCheckboxOptions,
  getTableExportFilename,
  mapCheckboxList,
} from "./utils";

export function useDataVisualizationData() {
  const intl = useIntl();
  const indicatorRefs = useRef([]);
  const elementPdf = useRef(null);
  const [exportToPDF] = useExportToPDF();

  const currentLocale = useSelector(({ theme }) => theme.locale);
  const dir = useSelector(({ theme }) => theme.direction);

  const [show, setShow] = useState(true);
  const { id, subId, indicatorId } = useParams();

  const [mainId, setMainId] = useState(id);
  const [indicatorDetailsValid, setIndicatorDetailsValid] = useState(false);
  const [subSubjectId, setSubSubjectId] = useState(subId);
  const [indicatorIdData, setIndicatorIdData] = useState(indicatorId);
  const [selectedIndicators, setSelectedIndicators] = useState([]);
  const [allChartData, setAllChartData] = useState([]);

  const mainList = useSWR({ url: END_POINTS.MAIN_SUBJECTS });
  const subSubjectsListWithMainId = useSWR({
    url: `${END_POINTS.SUBJECT_VISUALIZATION}/${mainId}`,
  });
  const indicatorsList = useSWR({
    url: `${END_POINTS.SUBJECT_DETAILS}/${subSubjectId}`,
  });

  useEffect(() => {
    setIndicatorIdData(indicatorId);
  }, [indicatorId]);

  const indicatorList = useMemo(
    () => flattenIndicators(indicatorsList),
    [
      indicatorsList.data,
      indicatorsList.error,
      indicatorsList.isLoading,
    ]
  );

  const selectedIndicator = useMemo(
    () => findSelectedIndicator(indicatorList, indicatorIdData),
    [indicatorList, indicatorIdData]
  );

  const [defaultVisualData, setDefaultVisualData] = useState({
    indicatorId: indicatorIdData,
    publicationId: null,
  });

  useEffect(() => {
    const timeOut = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
    return () => clearTimeout(timeOut);
  }, []);

  useEffect(() => {
    ReactGA.initialize(END_POINTS.TRACKING_ID);
  }, []);

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: `/data/mainSubject/:id/subSubject/:subId/data-visualization/:indicatorId`,
      title: `Main Subject: ${
        indicatorsList?.data?.data?.mainSubject?.title
      } - Sub Subject: ${indicatorsList?.data?.data?.title} - Indicator: ${
        selectedIndicator?.name
      }`,
    });
  }, [id, subId, selectedIndicator?.name]);

  useLayoutEffect(() => {
    if (
      indicatorIdData !== null &&
      indicatorRefs.current[indicatorIdData] &&
      !indicatorsList.error &&
      !indicatorsList.isLoading &&
      indicatorsList.data !== undefined &&
      indicatorsList.data.data !== undefined
    ) {
      indicatorRefs.current[indicatorIdData].scrollIntoView({
        behavior: indicatorIdData === indicatorId ? "instant" : "smooth",
        block: "nearest",
      });
    }
  }, [indicatorIdData, indicatorsList]);

  useEffect(() => {
    if (indicatorIdData && indicatorsList?.data?.data) {
      const publicationId = findIndicatorPublicationId(
        indicatorsList,
        indicatorIdData
      );
      if (publicationId != null) {
        setDefaultVisualData({
          indicatorId: indicatorIdData,
          publicationId,
        });
      }
    }
  }, [indicatorIdData, indicatorsList?.data?.data]);

  useEffect(() => {
    if (
      defaultVisualData.indicatorId !== null &&
      defaultVisualData.publicationId !== null &&
      defaultVisualData.indicatorId !== undefined &&
      defaultVisualData.publicationId !== undefined
    ) {
      setIndicatorDetailsValid(true);
    } else {
      setIndicatorDetailsValid(false);
    }
  }, [defaultVisualData.indicatorId, defaultVisualData.publicationId]);

  const visualizationDetails = useSWR({
    url:
      indicatorDetailsValid && defaultVisualData.indicatorId
        ? END_POINTS.VISUALIZATION_DETAILS
        : null,
    params: indicatorDetailsValid ? defaultVisualData : null,
  });

  const defaultState = {
    IndicatorId: indicatorIdData,
    SubSubjectId: subSubjectId,
  };

  const [filters, setFilters] = useState(defaultState);

  const indicatorData = useSWR({
    url: END_POINTS.INDICATOR_FILTER,
    params: filters,
  });

  const indicatorRows = indicatorData?.data?.data;
  const listOfCheckBox = mapCheckboxList(indicatorRows);
  const checkboxOptions = useMemo(
    () => getCheckboxOptions(indicatorRows),
    [indicatorRows]
  );

  const visualization = visualizationDetails?.data?.data;
  const periodicId = visualization?.periodic?.id;

  useEffect(() => {
    if (!indicatorRows) {
      setAllChartData([]);
      return;
    }

    if (selectedIndicators.length > 0) {
      setAllChartData(
        buildChartData({
          indicatorRows,
          selectedIndicators,
          periodicId,
          intl,
        })
      );
    } else {
      setAllChartData([]);
    }
  }, [
    selectedIndicators,
    currentLocale,
    indicatorRows,
    periodicId,
    intl,
  ]);

  const colHeaders = useMemo(
    () => buildColHeaders(allChartData, intl),
    [allChartData, intl]
  );

  const getChecked = (checkId) =>
    selectedIndicators.find((x) => x.id == checkId) != null;

  function handleCheckedChange(checkId, checked) {
    const indicator = listOfCheckBox?.find((x) => x.id == checkId);
    if (indicator == null) return;

    if (checked) {
      const found = selectedIndicators?.find((x) => x.id == checkId);
      if (found == null) {
        setSelectedIndicators([...selectedIndicators, indicator]);
      }
    } else {
      const item = selectedIndicators.find((x) => x.id == checkId);
      if (item != null) {
        setSelectedIndicators(
          selectedIndicators.filter((x) => x.id != checkId)
        );
      }
    }
  }

  const resetChartsData = () => {
    setAllChartData([]);
    setSelectedIndicators([]);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIndicators(
        checkboxOptions.map((item) => ({
          id: item.id,
          checked: true,
        }))
      );
    } else {
      resetChartsData();
    }
  };

  const handleMainSubjectChange = (value) => {
    setMainId(value);
    setFilters({});
    setDefaultVisualData({});
    resetChartsData();
    setSubSubjectId(subSubjectsListWithMainId?.data?.data[0]?.id);
  };

  const handleSubSubjectChange = (value) => {
    setSubSubjectId(value);
    setIndicatorIdData(1);
    setFilters({ ...defaultState, SubSubjectId: value });
    setDefaultVisualData({});
    resetChartsData();
  };

  const handleIndicatorChange = (value) => {
    setIndicatorIdData(value);
    setFilters({ ...defaultState, IndicatorId: value });
    setDefaultVisualData({
      ...defaultVisualData,
      indicatorId: value,
    });
    resetChartsData();
  };

  const handleDirFixedBar = () => {
    if (dir === "rtl") {
      document.querySelector(".fixed-sidebar").classList.toggle("r-active");
    } else {
      document.querySelector(".fixed-sidebar").classList.toggle("l-active");
    }
  };

  const handleDirContentFixedBar = () => {
    document
      .querySelector(".content-data-visualization .box-content")
      .classList.toggle("active");
    document
      .querySelector(".fixed-sidebar-box-content")
      .classList.toggle("active");
  };

  const tableExportFilename = getTableExportFilename(
    visualization,
    currentLocale
  );

  const tableExportTitle = LabelLangString({
    currentLocale,
    id: "name",
    defaultValue: visualization?.name,
    translations: visualization?.indicatorTranslations,
  });

  const hasExportableData = allChartData.length > 0;

  const handleChartPdfExport = () => {
    if (!hasExportableData) return;
    exportToPDF({
      ref: elementPdf,
      fileName: tableExportTitle,
      headerTitle: tableExportTitle,
    });
  };

  const handleChartPngExport = () => {
    if (!hasExportableData) return;
    downloadElementAsImage(
      elementPdf.current,
      getChartImageFilename(visualization, currentLocale, "png"),
      "png"
    );
  };

  const handleChartJpegExport = () => {
    if (!hasExportableData) return;
    downloadElementAsImage(
      elementPdf.current,
      getChartImageFilename(visualization, currentLocale, "jpg"),
      "jpeg"
    );
  };

  const indicatorPublication = findIndicatorPublication(
    indicatorsList,
    indicatorIdData
  );

  const isLoading =
    mainList.isLoading || subSubjectsListWithMainId.isLoading;

  return {
    intl,
    dir,
    currentLocale,
    isLoading,
    show,
    setShow,
    mainId,
    subSubjectId,
    indicatorIdData,
    setIndicatorIdData,
    indicatorRefs,
    elementPdf,
    mainList,
    subSubjectsListWithMainId,
    indicatorsList,
    indicatorList,
    selectedIndicator,
    indicatorData,
    listOfCheckBox,
    checkboxOptions,
    selectedIndicators,
    allChartData,
    colHeaders,
    visualizationDetails,
    visualization,
    indicatorPublication,
    getChecked,
    handleCheckedChange,
    handleSelectAll,
    handleMainSubjectChange,
    handleSubSubjectChange,
    handleIndicatorChange,
    handleDirFixedBar,
    handleDirContentFixedBar,
    tableExportFilename,
    tableExportTitle,
    hasExportableData,
    handleChartPdfExport,
    handleChartPngExport,
    handleChartJpegExport,
    excelImage: process.env.PUBLIC_URL + "/image/Excel.svg",
  };
}
